import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { ANY_SPORT_REWARDS_ESSENCE_QUALITY_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { sortBy } from '@sorare/core/src/lib/arrays';
import { scarcityNames } from '@sorare/core/src/lib/cards';
import { qualityNames } from '@sorare/core/src/lib/players';

import { Probability } from 'components/rewards/Probability';

import { EssenceOdds_cardShardsChestThreshold } from './__generated__/index.graphql';

const Line = styled(SBHorizontal)`
  border-radius: var(--unit);
  padding: var(--double-unit);
  background: var(--c-nd-50);
`;
const Probabilities = styled(Vertical).attrs({ gap: 1 })`
  --probability-height: calc(7 * var(--unit));
  min-height: calc((6 * var(--probability-height)) + (5 * var(--unit)));
`;

type Props = {
  rarity: Rarity;
  threshold: EssenceOdds_cardShardsChestThreshold;
};

export const EssenceOdds = ({ rarity, threshold }: Props) => {
  const { generateSportContextPath, sport } = useSportContext();
  const sortedProbabilities = sortBy(
    t => t.quality,
    [...threshold.probabilities]
  );

  return (
    <Vertical>
      <BodyM as="p" bold>
        <FormattedMessage
          id="Essence.EssenceOddsDialog.EssenceOdds.cardsOdds"
          defaultMessage="Cards odds"
        />
      </BodyM>
      <Probabilities>
        {sortedProbabilities.map(probability => (
          <Line
            key={probability.quality}
            as={Link}
            to={generateSportContextPath(
              ANY_SPORT_REWARDS_ESSENCE_QUALITY_RARITY,
              {
                sport,
                params: {
                  rarity,
                  quality: probability.quality,
                },
              }
            )}
            target="_blank"
          >
            <Horizontal>
              <ScarcityIcon scarcity={rarity} />
              <BodyM as="h3" bold>
                {scarcityNames[rarity]}
              </BodyM>
              <Chip>{qualityNames[probability.quality]}</Chip>
            </Horizontal>
            <div>
              <Probability probability={probability.basisPoints / 10000} />
            </div>
          </Line>
        ))}
      </Probabilities>
    </Vertical>
  );
};

EssenceOdds.fragments = {
  cardShardsChestThreshold: gql`
    fragment EssenceOdds_cardShardsChestThreshold on CardShardsChestThreshold {
      probabilities {
        basisPoints
        quality
      }
      threshold
    }
  ` as TypedDocumentNode<EssenceOdds_cardShardsChestThreshold>,
};
